import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ReactivePowerPhasesChart from "../components/ReactivePowerPhasesChart";
import ReactivePowerReportDialog from "../components/ReactivePowerReportDialog";


function breakLargeGaps(data, maxGapMs = 60 * 60 * 1000) {
  if (!data.length) return [];
  const result = [data[0]];
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1];
    const curr = data[i];
    const gap = curr.time - prev.time;
    if (gap > maxGapMs) {
      result.push({
        time: prev.time + 1,
        Qmean1: null, Qmean2: null, Qmean3: null, QTotal: null,
        Quad1_T: null, Quad2_T: null, Quad3_T: null, Quad4_T: null
      });
      result.push({
        time: curr.time - 1,
        Qmean1: null, Qmean2: null, Qmean3: null, QTotal: null,
        Quad1_T: null, Quad2_T: null, Quad3_T: null, Quad4_T: null
      });
    }
    result.push(curr);
  }
  return result;
}

export default function ReactivePowerPage({ deviceType = "analyzer" }) {
  const { t } = useTranslation();

  const { deviceId } = useParams();
  const saved = JSON.parse(localStorage.getItem("selectedDevice") || "{}");
  const isVirtual = saved.mode === "virtual";
  const targetId = isVirtual ? saved.virtualId : deviceId;

  const [data, setData] = useState([]);
  const [range, setRange] = useState("1D");
  const [reportOpen, setReportOpen] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const ENDPOINT_PREFIX = isVirtual
    ? "/virtual"
    : deviceType === "reactive"
      ? "/reactive"
      : "/measurements";


  function getRangeMs(r) {
    switch (r) {
      case "1H": return 1 * 60 * 60 * 1000;
      case "3H": return 3 * 60 * 60 * 1000;
      case "6H": return 6 * 60 * 60 * 1000;
      case "12H": return 12 * 60 * 60 * 1000;
      case "1D": return 1 * 24 * 60 * 60 * 1000;
      case "3D": return 3 * 24 * 60 * 60 * 1000;
      case "1W": return 7 * 24 * 60 * 60 * 1000;
      default: return 1 * 24 * 60 * 60 * 1000;
    }
  }

  async function fetchData() {
    try {
      const end = new Date();
      const start = new Date(end.getTime() - getRangeMs(range));
      const url = `${API_BASE}${ENDPOINT_PREFIX}/reactive-power?deviceId=${targetId}&start=${start.toISOString()}&end=${end.toISOString()}`;
      const res = await fetch(url);
      const rows = await res.json();

      const formatted = rows
        .filter(r => r.Qmean1 !== null || r.Qmean2 !== null || r.Qmean3 !== null) // ✅ null satırları at
        .map((r) => {
          const Q1 = r.Qmean1 ?? 0;
          const Q2 = r.Qmean2 ?? 0;
          const Q3 = r.Qmean3 ?? 0;

          return {
            time: new Date(r.time).getTime(),
            Qmean1: r.Qmean1,
            Qmean2: r.Qmean2,
            Qmean3: r.Qmean3,
            QTotal: Q1 + Q2 + Q3,
            Quad1_T: r.Quad1_T,
            Quad2_T: r.Quad2_T,
            Quad3_T: r.Quad3_T,
            Quad4_T: r.Quad4_T,
          };
        });

      setData(breakLargeGaps(formatted));
    } catch (err) {
      console.error("fetchData error:", err);
    }
  }

  useEffect(() => {
    if (!targetId) return;
    fetchData();
    const i = setInterval(fetchData, 5000);
    return () => clearInterval(i);
  }, [API_BASE, targetId, range]);

  return (
    <div className="p-4 md:p-6 grid grid-cols-1 gap-6">
      <div className="bg-white rounded-2xl shadow-md p-4 md:p-6 flex flex-col">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          {t('reactivePower.title')}
        </h2>
        <ReactivePowerPhasesChart
          data={data}
          range={range}
          onRangeChange={setRange}
          isVirtual={isVirtual}
        />
      </div>
      <div className="flex items-center justify-start mb-2">
        <button
          onClick={() => setReportOpen(true)}
          className="bg-sky-600 text-white px-3 py-2 rounded-md hover:bg-sky-700 text-sm"
        >
          {t('reactivePower.reportButton')}
        </button>
        <ReactivePowerReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          deviceId={targetId}
          deviceType={deviceType}
          API_BASE={API_BASE}
          isVirtual={isVirtual}

        />

      </div>

    </div>
  );
}
